import React, { useEffect, useState } from "react";
import { Input, Space, Form, Button, notification, Upload, Image, Select } from "antd";
import { AiOutlineRight } from "react-icons/ai";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import { GrUpload } from "react-icons/gr";
import WebBannerRequest from "./WebBannerRequest";

const getBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });

function WebBannerUpdate() {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { id } = useParams();

  // Separate states for both banners
  const [websiteFileList, setWebsiteFileList] = useState([]);
  const [mobileFileList, setMobileFileList] = useState([]);

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState("");

  const [bannerDetails, setBannerDetails] = useState({});
  const [submited, setSubmit] = useState(false);

  // ================== Preview Handler ==================
  const handlePreview = async (file) => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj);
    }
    setPreviewImage(file.url || file.preview);
    setPreviewOpen(true);
  };

  // ================== Change Handlers ==================
  const handleWebsiteChange = ({ fileList: newFileList }) => {
    setWebsiteFileList(newFileList);
  };

  const handleMobileChange = ({ fileList: newFileList }) => {
    setMobileFileList(newFileList);
  };

  const uploadButton = (
    <button
      style={{
        border: 0,
        background: "none",
      }}
    >
      <GrUpload />
      <div style={{ marginTop: 8 }}>Upload</div>
    </button>
  );

  // ================== Image Upload Function ==================
  const imageUpload = (file, name, fieldName) => {
    setLoading(true);

    const modifiedFile = new File([file], name, { type: file.type });
    const formData = new FormData();
    formData.append("file", modifiedFile);
    formData.append("fileName", name);

    WebBannerRequest.uploadImage(formData)
      .then((res) => {
        setLoading(false);
        if (res?.data?.uploadDirectory) {
          form.setFieldsValue({
            [fieldName]: res.data.uploadDirectory,
          });

          notification.success({
            message: `${fieldName === "imageUrl" ? "Website" : "Mobile"} banner uploaded successfully`,
          });
        }
      })
      .catch((e) => {
        setLoading(false);
        notification.error({ message: "File upload failed" });
        console.error(e);
      });
  };

  // ================== Props for Both Uploads ==================
  const websiteProps = {
    name: "file",
    multiple: false,
    accept: "image/*",
    beforeUpload: (file) => {
      const isImage = file.type.startsWith("image/");
      if (!isImage) {
        notification.error({
          message: "Invalid file type",
          description: "Only image files are allowed (jpg, png, jpeg, webp)",
        });
        return Upload.LIST_IGNORE;
      }
      imageUpload(file, file.name, "imageUrl");
      return false;
    },
    onRemove: () => {
      setWebsiteFileList([]);
      form.setFieldsValue({ imageUrl: null });
    },
  };

  const mobileProps = {
    name: "file",
    multiple: false,
    accept: "image/*",
    beforeUpload: (file) => {
      const isImage = file.type.startsWith("image/");
      if (!isImage) {
        notification.error({
          message: "Invalid file type",
          description: "Only image files are allowed (jpg, png, jpeg, webp)",
        });
        return Upload.LIST_IGNORE;
      }
      imageUpload(file, file.name, "mobileImageUrl");
      return false;
    },
    onRemove: () => {
      setMobileFileList([]);
      form.setFieldsValue({ mobileImageUrl: null });
    },
  };

  // ================== Fetch Banner Details ==================
  useEffect(() => {
    const getBannerDetails = async () => {
      setLoading(true);
      try {
        const res = await WebBannerRequest.getBannerDetail(id);
        const bannerData = res?.data?.data;

        if (bannerData) {
          const mappedData = {
            ...bannerData,
            status: bannerData?.status === 1 ? "1" : "0",
          };

          setBannerDetails(mappedData);
          form.setFieldsValue(mappedData);

          // Load existing images
          if (mappedData.imageUrl) {
            setWebsiteFileList([{ url: mappedData.imageUrl, name: "Website Banner" }]);
          }
          if (mappedData.mobileImageUrl) {
            setMobileFileList([{ url: mappedData.mobileImageUrl, name: "Mobile Banner" }]);
          }
        }
      } catch (e) {
        console.error("Error fetching banner details:", e);
      } finally {
        setLoading(false);
      }
    };

    if (id) getBannerDetails();
  }, [id, form]);

  const update = () => setSubmit(true);

  const onFinish = (values) => {
    if (!submited) return;

    setLoading(true);
    const formData = { ...values, id: id };

    WebBannerRequest.postUpdateBanner(formData)
      .then((res) => {
        setLoading(false);
        if (res.status === 200) {
          notification.success({ message: "Banner updated successfully" });
          navigate(-1, { state: { refresh: true } });
        } else {
          notification.error({ message: "Something went wrong" });
        }
      })
      .catch((e) => {
        setLoading(false);
        notification.error({ message: "Failed to update banner" });
      });
  };

  return (
    <div>
      <section>
        <div className="Create-Category">
          <Form.Item className="submit-btn">
            <Space>
              <Button className="f-sub-btn btn-width" onClick={() => navigate(-1)} disabled={loading}>
                <ArrowLeftOutlined />
              </Button>
            </Space>
          </Form.Item>
          <h3>Update Banner</h3>

          <Form
            layout="vertical"
            className="subscription-form"
            form={form}
            onFinish={onFinish}
          >
            <div className="subscription">
              <Form.Item
                label="Banner Name"
                name="bannerName"
                className="subscription-plan"
                   rules={[
                                 { required: true, message: "Please enter banner name" },
                                 {
                                   pattern:/^[a-z0-9-]+$/,
                                   message: "banner Name can contain only lowercase letters, numbers, and hyphens",
                                 },
                               ]}
              >
                <Input size="large" placeholder="Enter banner Name" />
              </Form.Item>

              <Form.Item
                label="Status"
                name="status"
                className="subscription-plan"
                rules={[{ required: true, message: "Please select status" }]}
              >
                <Select
                  placeholder="Select status"
                  options={[
                    { value: "0", label: "Inactive" },
                    { value: "1", label: "Active" },
                  ]}
                />
              </Form.Item>
            </div>

            <div className="subscription">
              <Form.Item
                label="Page Source"
                name="pageKey"
                className="subscription-plan"
                rules={[{ required: true, message: "Please enter page source" }]}
              >
                <Input size="large" placeholder="Enter page source" />
              </Form.Item>

              <Form.Item
                label="Banner Key"
                name="bannerKey"
                className="subscription-plan"
                rules={[
                  { pattern: /^[a-z0-9-]+$/, message: "Only lowercase letters, numbers and hyphens allowed" },
                ]}
              >
                <Input size="large" placeholder="Enter banner key" />
              </Form.Item>
            </div>

            <div className="subscription">
              <Form.Item
                label="Order By"
                name="orderBy"
                className="subscription-plan"
                rules={[{ required: true, message: "Please select order" }]}
              >
                <Select
                  placeholder="Select order"
                  options={[
                    { value: "1", label: "1" },
                    { value: "2", label: "2" },
                    { value: "3", label: "3" },
                    { value: "4", label: "4" },
                    { value: "5", label: "5" },
                  ]}
                />
              </Form.Item>
            </div>

            {/* Website Banner Upload - Class Preserved */}
            <div className="upload-Img">
              <Form.Item
                label="Upload website banner"
                name="imageUrl"
                className="subscription-plan"
                rules={[
                  { required: true, message: "Please upload the website banner" },
                ]}
              >
                <Upload
                  {...websiteProps}
                  maxCount={1}
                  listType="picture-card"
                  fileList={websiteFileList}
                  className="upload-Img-input"
                  onPreview={handlePreview}
                  onChange={handleWebsiteChange}
                >
                  {websiteFileList.length >= 1 ? null : uploadButton}
                </Upload>
              </Form.Item>
            </div>

            {/* Mobile Banner Upload - Class Preserved */}
            {/* <div className="upload-Img">
              <Form.Item
                label="Upload mobile banner"
                name="mobileImageUrl"
                className="subscription-plan"
                rules={[
                  { required: true, message: "Please upload the mobile banner" },
                ]}
              >
                <Upload
                  {...mobileProps}
                  maxCount={1}
                  listType="picture-card"
                  fileList={mobileFileList}
                  className="upload-Img-input"
                  onPreview={handlePreview}
                  onChange={handleMobileChange}
                >
                  {mobileFileList.length >= 1 ? null : uploadButton}
                </Upload>
              </Form.Item>
            </div> */}

            <div className="bottom-button">
              <div style={{ padding: "10px" }}>
                <Button 
                  type="primary" 
                  htmlType="submit" 
                  loading={loading} 
                  onClick={update}
                  className="f-sub-btn btn-width"
                >
                  Save & Proceed
                  <span>
                    <AiOutlineRight />
                  </span>
                </Button>
              </div>
            </div>
          </Form>
        </div>
      </section>

      {/* Preview Image */}
      {previewImage && (
        <Image
          wrapperStyle={{ display: "none" }}
          preview={{
            visible: previewOpen,
            onVisibleChange: (visible) => setPreviewOpen(visible),
            afterOpenChange: (visible) => !visible && setPreviewImage(""),
          }}
          src={previewImage}
        />
      )}
    </div>
  );
}

export default WebBannerUpdate;